'use client';

import type { PropsWithChildren} from 'react';
import { Fragment, useCallback, useRef } from 'react';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';

import { useReferralUrlQuery } from '@query/user';

import { type ApplicationHotOfferSchemaPayload } from '@api/types/applications';

import type { ActivityHandle } from '@ui/uikit/components/common/Activity';
import { Activity } from '@ui/uikit/components/common/Activity';
import { Button } from '@ui/uikit/components/common/Button';
import { useDropdownMenu } from '@ui/uikit/components/common/DropdownMenu';
import { SharingDropdownMenuController } from '@ui/uikit/components/common/SharingDropdownMenu';
import { Share } from '@ui/uikit/components/icons/mono/Share';
import { formatAmount } from '@ui/uikit/utils';

import { useHotOfferShareConfig } from '../hooks/useHotOfferShareConfig';

interface HotOfferSharingControllerProps {
  offer: ApplicationHotOfferSchemaPayload;
}

const FALLBACK_SHARING_URL = typeof window != 'undefined' ? window.document.URL : undefined;

const HotOfferSharingController = ({
  offer,
  children,
}: PropsWithChildren<HotOfferSharingControllerProps>) => {
  const { name, activePayoutToken, offerId } = offer;

  const { isAuth } = useAuth();

  const { data: referralUrl } = useReferralUrlQuery(offerId, 'hot-offer', {
    enabled: isAuth,
  });

  const sharingLink = referralUrl?.sharingLink || FALLBACK_SHARING_URL;

  const platformConfigurations = useHotOfferShareConfig({
    url: sharingLink || '',
    name: name ?? '{{OFFER_NAME}}',
    amount: formatAmount(Number((activePayoutToken && activePayoutToken.amount) ?? 0)),
    currency: activePayoutToken?.symbol || '',
  });

  return (
    <SharingDropdownMenuController
      url={sharingLink}
      platforms={['X', 'Discord', 'Telegram', 'Facebook', 'Reddit', 'LinkedIn', 'Email']}
      title="Share offer with friends"
      messageTemplate={platformConfigurations}
    >
      {children}
    </SharingDropdownMenuController>
  );
};

const HotOfferSharingControl = () => {
  const { openMenu } = useDropdownMenu();

  const { user } = useAuth();

  const { open: openAuthModal } = useAuthModal();

  const button = useRef<HTMLButtonElement & HTMLAnchorElement>(null);

  const handleClick = useCallback(async () => {
    if (!flow.current) {
      console.warn(`Flow handle not initialized.`);

      return;
    }

    if (!button.current) {
      console.warn(`Button handle not initialized.`);

      return;
    }

    await flow.current.initiate();

    openMenu(button.current);
  }, [openMenu]);

  const flow = useRef<ActivityHandle>(null);

  const signIn = useCallback(() => {
    openAuthModal();
  }, [openAuthModal]);

  return (
    <Fragment>
      <Activity ref={flow}>
        <Activity.Step requirement={!!user} action={signIn} key="signin" />
      </Activity>

      <Button variant="secondary" size="large" onClick={handleClick} ref={button}>
        <Share color="var(--text-color-tertiary)" />
      </Button>
    </Fragment>
  );
};

export { HotOfferSharingControl, HotOfferSharingController };
